import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { TABLE_ROW_HEIGHT } from '@local/finsera-core/src/theme/layout-size';

import { formatData, prepareDataSummaryStats } from './helpers';
import { Params } from './types';
import { radarChartFormat } from '../../common/formatters/radar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  portfolios: string;
  fullScreen: boolean;
  params: Params;
  visualization: 'chart' | 'table';
  context: ts.types.basket.BasketAnalysisContext;
  title: string;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  portfolios,
  visualization,
  params,
  context,
  fullScreen,
  title,
}: ChartProps) => {
  const baskets = useSelector((state) => state.resources.baskets);

  const [data, setData] = React.useState<{
    chart: ts.types.widgets.RadarData;
    table: ts.types.widgets.TableData;
    dimensions: string[];
  }>(null);
  const [error, setError] = React.useState(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: portfolios,
          rename: { metric: 'spoke' },
        },
      });

      const formattedData = formatData(response.data);
      const radarChartData = radarChartFormat(formattedData);
      if (response) setData(prepareDataSummaryStats(radarChartData, params, context, baskets));
    } catch (err) {
      setError(JSON.stringify(err));
    }
  };

  React.useEffect(() => {
    if (portfolios) loadData();
    return () => setData(null);
  }, [portfolios]);

  const getColumns = React.useMemo(() => {
    if (!data?.table || !data?.dimensions) return [];

    return ['spoke', ...data.dimensions].map((field) => ({
      field,
      headerName: field,
      flex: 1,
    })) as mui.dataGrid.GridColDef[];
  }, [data]);

  return (
    <>
      <mui.core.Box display={visualization == 'chart' ? 'inline' : 'none'}>
        <ui.RadarChart
          id={id}
          data={data?.chart}
          labels={data?.table}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          dimensions={data?.dimensions}
          fullScreen={fullScreen}
          readOnly={readOnly}
          exportTitle={title}
        />
      </mui.core.Box>
      <mui.core.Box display={visualization == 'table' ? 'inline' : 'none'}>
        {!_.isNull(data) && (
          <ui.FinDataGrid
            rows={data?.table || []}
            columns={getColumns}
            autoHeight
            hideFooter
            disableColumnMenu
            disableColumnFilter
            getRowId={(row) => row.spoke}
            rowHeight={TABLE_ROW_HEIGHT.sm}
            columnHeaderHeight={TABLE_ROW_HEIGHT.sm}
          />
        )}
      </mui.core.Box>
    </>
  );
};

export default Chart;
